import { useEffect, useRef, useState } from 'react';
import { playTone, ensureAudioUnlocked, playButtonClick } from '../utils/audio';

export default function BeltMilestoneAnimation({ user, belt, onComplete }) {
  const [scale, setScale] = useState(0);
  const [animationFinished, setAnimationFinished] = useState(false);
  const soundPlayedRef = useRef(false);

  useEffect(() => {
    // Use ref to prevent double sound play
    if (soundPlayedRef.current) return;
    soundPlayedRef.current = true;

    const playMilestoneSound = async () => {
      try {
        await ensureAudioUnlocked();
        const notes = [
          { freq: 587, dur: 0.2 },  // D5
          { freq: 659, dur: 0.2 },  // E5
          { freq: 784, dur: 0.25 }, // G5
          { freq: 1047, dur: 0.4 }, // C6 - POWER NOTE
          { freq: 932, dur: 0.15 }, // A5
          { freq: 1047, dur: 0.15 }, // C6
          { freq: 1175, dur: 0.6 }, // D6 - CLIMAX
        ];
        
        for (let i = 0; i < notes.length; i++) {
          setTimeout(() => {
            playTone(notes[i].freq, notes[i].dur, 0.12);
          }, i * 250);
        }
      } catch (err) {
        console.warn('Sound failed:', err);
      }
    };

    // All animations start simultaneously
    setScale(1);
    playMilestoneSound();

    // Show button after 2.2 seconds
    const finishTimer = setTimeout(() => {
      setAnimationFinished(true);
    }, 2200);

    return () => clearTimeout(finishTimer);
  }, []);

  const profileImage = user?.profileImage || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"%3E%3Ccircle cx="50" cy="50" r="50" fill="%23e5e7eb"/%3E%3Ccircle cx="50" cy="35" r="15" fill="%239ca3af"/%3E%3Cellipse cx="50" cy="70" rx="20" ry="18" fill="%239ca3af"/%3E%3C/svg%3E';
  const glowColor = belt?.color || '#fb923c';
  const textColor = belt?.text || 'white';

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.85)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 9999,
      padding: '16px',
      overflow: 'hidden'
    }}>
      <style>{`
        @keyframes scaleIn {
          from { transform: scale(0) rotateZ(-180deg); opacity: 0; }
          to { transform: scale(1) rotateZ(0deg); opacity: 1; }
        }
        @keyframes fallDown {
          from { transform: translateY(-100px); opacity: 1; }
          to { transform: translateY(80vh); opacity: 0; }
        }
        @keyframes spinBelt {
          from { transform: rotateZ(0deg); }
          to { transform: rotateZ(360deg); }
        }
        @keyframes glow {
          0%, 100% { filter: drop-shadow(0 0 20px rgba(251, 146, 60, 0.6)); }
          50% { filter: drop-shadow(0 0 40px rgba(251, 146, 60, 1)); }
        }
      `}</style>

      {/* Falling Stars */}
      {[...Array(12)].map((_, i) => (
        <div key={`star-${i}`} style={{
          position: 'absolute',
          fontSize: 'clamp(20px, 5vw, 32px)',
          animation: scale > 0 ? `fallDown ${2 + i * 0.3}s linear forwards` : 'none',
          left: `${(i / 12) * 100}%`,
          top: '-50px',
          pointerEvents: 'none'
        }}>⭐</div>
      ))}

      {/* Main Content */}
      <div style={{
        textAlign: 'center',
        transform: `scale(${scale})`,
        transition: 'transform 1.2s cubic-bezier(0.34, 1.56, 0.64, 1)',
        filter: scale > 0 ? `drop-shadow(0 0 30px rgba(251, 146, 60, 0.5))` : 'none'
      }}>
        {/* NEW BELT RANK - TOP */}
        <div style={{
          fontSize: 'clamp(22px, 5vw, 28px)',
          color: '#fbbf24',
          fontWeight: 'bold',
          margin: '0 0 clamp(16px, 2vw, 20px) 0',
          textShadow: '0 2px 8px rgba(0, 0, 0, 0.8), 0 0 6px rgba(255, 191, 36, 0.4)',
          animation: scale > 0 ? 'scaleIn 1s cubic-bezier(0.34, 1.56, 0.64, 1)' : 'none'
        }}>
          🎖️ NEW BELT RANK! 🎖️
        </div>

        {/* Profile Picture with Belt Behind */}
        <div style={{
          position: 'relative',
          width: 'clamp(120px, 30vw, 160px)',
          height: 'clamp(120px, 30vw, 160px)',
          margin: '0 auto clamp(16px, 2vw, 20px)',
          animation: scale > 0 ? 'scaleIn 1s cubic-bezier(0.34, 1.56, 0.64, 1) 0.2s both' : 'none'
        }}>
          {/* Belt behind profile - Real belt visual with diagonal stripes */}
          <div style={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            backgroundColor: belt?.color || '#fb923c',
            backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.12) 0 6px, rgba(255,255,255,0) 6px 12px)',
            border: `3px solid ${belt?.color || '#fb923c'}`,
            boxShadow: '0 0 12px rgba(0,0,0,0.35), 0 0 16px ' + (belt?.color ? 'rgba(' + belt.color + ',0.35)' : 'rgba(251, 146, 60, 0.35)'),
            animation: scale > 0 ? 'spinBelt 4s linear infinite' : 'none',
            zIndex: 0,
            inset: 0
          }}>
            {/* Stripe accent on the side */}
            <span style={{
              position: 'absolute',
              right: '15%',
              top: '50%',
              width: '25%',
              height: '40%',
              backgroundColor: belt?.stripe || '#fbbf24',
              borderLeft: '2px solid #000000',
              transform: 'translateY(-50%)',
              boxShadow: 'inset 0 0 6px rgba(0,0,0,0.3)'
            }} />
            {/* Shine effect */}
            <span style={{
              position: 'absolute',
              left: '15%',
              top: '15%',
              width: '20%',
              height: '20%',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(255,255,255,0.9), rgba(255,255,255,0.0))',
              opacity: 0.8,
              boxShadow: 'inset 0 0 8px rgba(255,255,255,0.3)'
            }} />
          </div>
          
          {/* Profile image */}
          <div style={{
            position: 'absolute',
            inset: 6,
            borderRadius: '50%',
            border: `4px solid ${glowColor}`,
            overflow: 'hidden',
            boxShadow: `0 0 30px ${glowColor}, inset 0 0 20px rgba(0,0,0,0.2)`,
            animation: scale > 0 ? 'glow 1s ease-in-out infinite' : 'none',
            backgroundColor: '#f0f0f0',
            zIndex: 1
          }}>
            <img
              src={profileImage}
              alt="Profile"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover'
              }}
            />
          </div>
        </div>

        {/* Username - Below Profile */}
        <h1 style={{
          fontSize: 'clamp(28px, 7vw, 44px)',
          fontWeight: 'bold',
          color: '#ffffff',
          margin: 'clamp(12px, 2vw, 16px) 0 clamp(16px, 2vw, 20px) 0',
          textShadow: '0 2px 10px rgba(0, 0, 0, 0.8), 0 0 6px rgba(255, 255, 255, 0.3)',
          animation: scale > 0 ? 'scaleIn 1s cubic-bezier(0.34, 1.56, 0.64, 1) 0.3s both' : 'none'
        }}>
          {user?.displayName || 'Champion'}
        </h1>

        {/* Rank: Belt Color Text */}
        <div style={{
          fontSize: 'clamp(18px, 4vw, 24px)',
          fontWeight: 'bold',
          margin: '0 0 clamp(12px, 2vw, 16px) 0',
          animation: scale > 0 ? 'scaleIn 1s cubic-bezier(0.34, 1.56, 0.64, 1) 0.4s both' : 'none'
        }}>
          <span style={{
            color: '#ffffff',
            textShadow: '0 1px 4px rgba(0, 0, 0, 0.6)'
          }}>
            Rank:
          </span>
          {' '}
          <span style={{
            color: 'transparent',
            backgroundImage: `linear-gradient(180deg, #ffffff 0%, #ffffff 75%, ${belt?.color || '#fb923c'} 100%)`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            letterSpacing: '0.5px',
            fontWeight: 'bold'
          }}>
            {belt?.name || 'Elite Belt'}
          </span>
        </div>

        {/* Horizontal Belt Line */}
        <div style={{
          width: 'clamp(160px, 40vw, 220px)',
          height: 'clamp(14px, 3vw, 18px)',
          borderRadius: '8px',
          backgroundColor: belt?.color || '#fb923c',
          backgroundImage: 'repeating-linear-gradient(45deg, rgba(255,255,255,0.12) 0 6px, rgba(255,255,255,0) 6px 12px)',
          border: '2px solid rgba(255, 255, 255, 0.4)',
          position: 'relative',
          margin: 'clamp(16px, 2vw, 20px) auto clamp(20px, 3vw, 28px)',
          boxShadow: '0 0 10px rgba(0,0,0,0.4), 0 0 8px ' + (belt?.color ? 'rgba(' + belt.color + ',0.4)' : 'rgba(251, 146, 60, 0.4)'),
          animation: scale > 0 ? 'scaleIn 1s cubic-bezier(0.34, 1.56, 0.64, 1) 0.5s both' : 'none',
          overflow: 'hidden'
        }}>
          {/* Stripe accent */}
          <span style={{
            position: 'absolute',
            right: '12%',
            top: 0,
            bottom: 0,
            width: '20%',
            backgroundColor: belt?.stripe || '#fbbf24',
            borderLeft: '2px solid rgba(255, 255, 255, 0.3)',
            boxShadow: 'inset 0 0 4px rgba(0,0,0,0.3)'
          }} />
          {/* Shine */}
          <span style={{
            position: 'absolute',
            left: '12%',
            top: '50%',
            width: '15%',
            height: '60%',
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(255,255,255,0.8), rgba(255,255,255,0.0))',
            opacity: 0.7,
            transform: 'translateY(-50%)'
          }} />
        </div>

        {/* Message */}
        <div style={{
          fontSize: 'clamp(16px, 4vw, 20px)',
          color: '#e0e7ff',
          fontWeight: '600',
          textShadow: '0 1px 5px rgba(0, 0, 0, 0.8), 0 0 4px rgba(255, 255, 255, 0.3)',
          letterSpacing: '0.5px',
          marginBottom: animationFinished ? 'clamp(16px, 2vw, 24px)' : 'clamp(12px, 2vw, 16px)',
          animation: scale > 0 ? 'scaleIn 1s cubic-bezier(0.34, 1.56, 0.64, 1) 0.6s both' : 'none'
        }}>
          ✨ Keep crushing it! ✨
        </div>

        {/* Continue Button */}
        {animationFinished && (
          <button
            onClick={() => {
              playButtonClick();
              onComplete();
            }}
            style={{
              animation: 'floatUp 0.5s ease-out',
              padding: 'clamp(12px, 3vw, 16px) clamp(24px, 5vw, 32px)',
              fontSize: 'clamp(16px, 4vw, 20px)',
              fontWeight: 'bold',
              backgroundColor: glowColor,
              color: textColor,
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              boxShadow: `0 4px 15px rgba(0, 0, 0, 0.3)`,
              textShadow: '0 1px 4px rgba(0, 0, 0, 0.3)',
              transition: 'all 0.2s',
              marginTop: 'clamp(16px, 3vw, 24px)',
              WebkitTouchCallout: 'none',
              WebkitUserSelect: 'none'
            }}
            onMouseOver={(e) => {
              e.target.style.transform = 'scale(1.05)';
              e.target.style.boxShadow = `0 6px 20px rgba(0, 0, 0, 0.4)`;
            }}
            onMouseOut={(e) => {
              e.target.style.transform = 'scale(1)';
              e.target.style.boxShadow = `0 4px 15px rgba(0, 0, 0, 0.3)`;
            }}
            onTouchStart={(e) => {
              e.target.style.transform = 'scale(1.02)';
            }}
            onTouchEnd={(e) => {
              e.target.style.transform = 'scale(1)';
            }}
          >
            Continue 🎖️
          </button>
        )}
      </div>
    </div>
  );
}
